import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import PublicLayout from "@/components/layout/PublicLayout";
import { ArrowRight, Terminal, Inbox, PackageOpen, Mail, Eye, MousePointerClick, RefreshCw, TrendingUp } from "lucide-react";

const StatTile = ({ label, value, Icon, accent = false, testid }) => (
  <div className="ft-card" data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <div className="text-[0.65rem] uppercase tracking-[0.22em] font-semibold text-[var(--ft-muted)]">{label}</div>
      <Icon size={14} className={accent ? "text-[var(--ft-gold)]" : "text-[var(--ft-muted)]"} strokeWidth={1.5} />
    </div>
    <div className={`text-3xl font-bold tracking-tight ${accent ? "text-[var(--ft-gold)]" : "text-[var(--ft-black)]"}`}>{value ?? "—"}</div>
  </div>
);

const AdminLandingDashboard = ({ user, isAdmin, logout }) => {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = () => {
    setRefreshing(true);
    api.get("/admin/today")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load today's snapshot"))
      .finally(() => { setLoading(false); setRefreshing(false); });
  };

  useEffect(() => { load(); }, []);

  const fmtTime = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }); }
    catch { return ""; }
  };

  const kindPill = {
    contact:      { color: "#8a6d1a", bg: "#fbf3dc", label: "Contact" },
    sample:       { color: "#5a4712", bg: "#f6e9be", label: "Sample" },
    consultation: { color: "#0f6f3a", bg: "#e6f4ec", label: "Consult" },
  };

  return (
    <PublicLayout>
      <section className="py-16 bg-[var(--ft-light-gray)] min-h-[70vh]">
        <div className="ft-container max-w-5xl">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
            <div>
              <div className="text-[0.6875rem] uppercase tracking-[0.32em] text-[var(--ft-gold)] font-semibold mb-3 flex items-center gap-2">
                <Terminal size={12} /> Command Center · Welcome back, {user.full_name?.split(" ")[0] || "Founder"}
              </div>
              <span className="ft-gold-rule-lg" />
              <h1 className="mt-5 text-[clamp(2rem,4vw,3rem)] font-bold tracking-tight leading-[1.05]">
                Here&apos;s your <span className="ft-serif text-[var(--ft-gold)]">last 24 hours</span>.
              </h1>
              <p className="mt-3 text-[var(--ft-muted)]">Everything that landed in the funnel since yesterday. Enter the Command Center for the full view.</p>
            </div>
            <div className="flex gap-2">
              <button onClick={load} disabled={refreshing} data-testid="landing-refresh" className="ft-btn-ghost inline-flex items-center gap-1.5">
                <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} /> Refresh
              </button>
              <button onClick={() => { logout(); nav("/"); }} data-testid="landing-signout" className="ft-btn-ghost">Sign out</button>
            </div>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-4 gap-4">
              {[0,1,2,3].map((i) => (
                <div key={i} className="ft-card animate-pulse">
                  <div className="h-3 w-20 bg-[var(--ft-light-gray)] rounded mb-3" />
                  <div className="h-8 w-16 bg-[var(--ft-light-gray)] rounded" />
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4" data-testid="landing-stats">
                <StatTile label="Leads Today"      value={data?.leads_today ?? 0}          Icon={Inbox}             accent testid="stat-leads-today" />
                <StatTile label="Sample Requests"  value={data?.sample_requests_today ?? 0} Icon={PackageOpen}       accent testid="stat-samples-today" />
                <StatTile label="Emails Sent"      value={data?.emails_sent_today ?? 0}     Icon={Mail}              testid="stat-emails-sent" />
                <StatTile label="Emails Opened"    value={data?.emails_opened_today ?? 0}   Icon={Eye}               accent testid="stat-emails-opened" />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <StatTile label="Contacts"          value={data?.contacts_today ?? 0}       Icon={Inbox}             testid="stat-contacts" />
                <StatTile label="Consultations"    value={data?.consultations_today ?? 0}   Icon={TrendingUp}        testid="stat-consultations" />
                <StatTile label="Emails Delivered" value={data?.emails_delivered_today ?? 0} Icon={Mail}             testid="stat-emails-delivered" />
                <StatTile label="Emails Clicked"   value={data?.emails_clicked_today ?? 0}  Icon={MousePointerClick} testid="stat-emails-clicked" />
              </div>

              {data?.recent?.length > 0 && (
                <div className="ft-card p-0 overflow-hidden mb-8" data-testid="landing-recent">
                  <div className="px-5 py-4 border-b border-[var(--ft-border)] flex items-center justify-between">
                    <div className="text-[0.7rem] uppercase tracking-[0.24em] font-semibold text-white">Latest activity</div>
                    <div className="text-[0.65rem] uppercase tracking-widest text-white/40">Newest first</div>
                  </div>
                  <ul className="divide-y divide-[var(--ft-border)]">
                    {data.recent.map((r, i) => {
                      const pill = kindPill[r.kind] || kindPill.contact;
                      return (
                        <li key={i} className="px-5 py-3 flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-white truncate">{r.label}</div>
                            {r.sub && <div className="text-xs text-white/50 truncate">{r.sub}</div>}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="text-[0.6rem] font-bold uppercase tracking-[0.18em] px-2 py-1 rounded-full" style={{ background: pill.bg, color: pill.color }}>{pill.label}</span>
                            <span className="text-[0.7rem] text-white/40 w-16 text-right">{fmtTime(r.at)}</span>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3">
                <button onClick={() => nav(isAdmin ? "/admin" : "/portal")} data-testid="landing-enter-cc" className="ft-btn-primary inline-flex items-center gap-2">
                  <Terminal size={15} strokeWidth={1.75} />
                  Enter {isAdmin ? "Command Center" : "Client Portal"}
                  <ArrowRight size={15} />
                </button>
                <Link to="/admin/email-log" data-testid="landing-email-log" className="ft-btn-ghost inline-flex items-center gap-2"><Eye size={13}/> View Email Log</Link>
                <Link to="/admin/samples"   data-testid="landing-samples"   className="ft-btn-ghost inline-flex items-center gap-2"><PackageOpen size={13}/> Sample Requests</Link>
              </div>
            </>
          )}
        </div>
      </section>
    </PublicLayout>
  );
};

export const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { user, isAdmin, login, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const redirect = loc.state?.from || null;

  // Already-authenticated admins → landing preview dashboard (not the login form)
  if (user && isAdmin) {
    return <AdminLandingDashboard user={user} isAdmin={isAdmin} logout={logout} />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.full_name.split(" ")[0]}.`);
      if (redirect) nav(redirect);
      else nav(u.role === "client" ? "/portal" : "/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <PublicLayout>
      <section className="py-20 min-h-[70vh] flex items-center bg-[var(--ft-light-gray)]">
        <div className="ft-container max-w-md w-full">
          <div className="text-[0.6875rem] uppercase tracking-[0.32em] text-[var(--ft-gold)] font-semibold mb-4 flex items-center gap-2"><Terminal size={12}/> Command Center</div>
          <span className="ft-gold-rule-lg" />
          <h1 className="mt-6 text-4xl font-bold tracking-tight mb-8">Sign in to your <span className="ft-serif text-[var(--ft-gold)]">portal</span>.</h1>
          <form onSubmit={submit} className="ft-card space-y-5" data-testid="login-form">
            <div>
              <label className="ft-label">Email</label>
              <input required type="email" data-testid="login-email" className="ft-input" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div>
              <label className="ft-label">Password</label>
              <input required type="password" data-testid="login-password" className="ft-input" value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            <button type="submit" disabled={loading} data-testid="login-submit" className="ft-btn-primary w-full justify-center">
              {loading ? "Signing in…" : "Sign In"} <ArrowRight size={16} />
            </button>
            <div className="text-sm text-center">
              <Link to="/forgot-password" className="text-[var(--ft-muted)] hover:text-[var(--ft-black)]" data-testid="login-forgot">
                Forgot your password?
              </Link>
            </div>
            <div className="text-sm text-center text-[var(--ft-muted)]">
              Don&apos;t have an account? <Link to="/register" className="font-semibold" style={{ color: "var(--ft-gold)" }}>Create one</Link>
            </div>
          </form>
        </div>
      </section>
    </PublicLayout>
  );
};

export const Register = () => {
  const [form, setForm] = useState({ full_name: "", email: "", password: "", company: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const nav = useNavigate();
  const update = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await register(form);
      toast.success(`Welcome, ${user.full_name.split(" ")[0]}.`);
      nav("/portal");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally { setLoading(false); }
  };

  return (
    <PublicLayout>
      <section className="py-20 bg-[var(--ft-light-gray)]">
        <div className="ft-container max-w-md w-full">
          <div className="text-[0.6875rem] uppercase tracking-[0.32em] text-[var(--ft-gold)] font-semibold mb-4">Client Registration</div>
          <span className="ft-gold-rule-lg" />
          <h1 className="mt-6 text-4xl font-bold tracking-tight mb-8">Create your <span className="ft-serif text-[var(--ft-gold)]">FLOWTALIS</span> account.</h1>
          <form onSubmit={submit} className="ft-card space-y-5" data-testid="register-form">
            <div>
              <label className="ft-label">Full Name*</label>
              <input required data-testid="register-name" className="ft-input" value={form.full_name} onChange={(e) => update("full_name", e.target.value)} />
            </div>
            <div>
              <label className="ft-label">Email*</label>
              <input required type="email" data-testid="register-email" className="ft-input" value={form.email} onChange={(e) => update("email", e.target.value)} />
            </div>
            <div>
              <label className="ft-label">Password* <span className="font-normal text-[var(--ft-muted)]">(min 8 characters)</span></label>
              <input required minLength={8} type="password" data-testid="register-password" className="ft-input" value={form.password} onChange={(e) => update("password", e.target.value)} />
            </div>
            <div>
              <label className="ft-label">Company / Brand</label>
              <input data-testid="register-company" className="ft-input" value={form.company} onChange={(e) => update("company", e.target.value)} />
            </div>
            <div>
              <label className="ft-label">Phone</label>
              <input data-testid="register-phone" className="ft-input" value={form.phone} onChange={(e) => update("phone", e.target.value)} />
            </div>
            <button type="submit" disabled={loading} data-testid="register-submit" className="ft-btn-primary w-full justify-center">
              {loading ? "Creating account…" : "Create Account"}
            </button>
            <div className="text-sm text-center text-[var(--ft-muted)]">
              Already have an account? <Link to="/login" className="font-semibold" style={{ color: "var(--ft-gold)" }}>Sign in</Link>
            </div>
          </form>
        </div>
      </section>
    </PublicLayout>
  );
};
